import { Component, Input, Output, EventEmitter, HostBinding, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule, MatCheckboxChange } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { ImageInfo } from '../../../services/image-processing-tool.service';

@Component({
  selector: 'app-thumbnail-item',
  standalone: true,
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatBadgeModule,
    MatMenuModule,
    MatDividerModule
  ],
  templateUrl: './thumbnail-item.component.html',
  styleUrls: ['./thumbnail-item.component.scss']
})
export class ThumbnailItemComponent {
  @Input() image!: ImageInfo;
  @Input() isSelected: boolean = false;
  @Input() selectionOrder: number = 0;
  @Input() isActive: boolean = false;
  @Input() index: number = 0;
  
  @Output() selectionChanged = new EventEmitter<boolean>();
  @Output() thumbnailClicked = new EventEmitter<void>();
  @Output() filenameClicked = new EventEmitter<void>();
  @Output() deleteClicked = new EventEmitter<void>();
  @Output() excludeClicked = new EventEmitter<void>();
  @Output() openExternalClicked = new EventEmitter<void>();
  @Output() previewClicked = new EventEmitter<void>();
  
  @ViewChild('contextMenuTrigger') contextMenuTrigger!: MatMenuTrigger;
  
  @HostBinding('class.selected') get selectedClass() { return this.isSelected; }
  @HostBinding('class.active') get activeClass() { return this.isActive; }
  
  // Context menu position
  contextMenuPosition = { x: '0px', y: '0px' };
  
  onCheckboxChange(event: MatCheckboxChange): void {
    this.selectionChanged.emit(event.checked);
  }
  
  onThumbnailClick(event: Event): void {
    event.stopPropagation();
    this.thumbnailClicked.emit();
  }
  
  onFilenameClick(event: Event): void {
    event.stopPropagation();
    this.filenameClicked.emit();
  }
  
  onContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';
    this.contextMenuTrigger.openMenu();
  }
  
  onSnagitClick(event: Event): void {
    event.stopPropagation();
    this.openExternalClicked.emit();
  }
  
  onPreviewClick(event: Event): void {
    event.stopPropagation();
    this.previewClicked.emit();
  }
  
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClicked.emit();
  }
  
  onExcludeClick(): void {
    this.excludeClicked.emit();
  }
  
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = 'assets/placeholder-image.svg';
    }
  }
  
  get displayName(): string {
    if (!this.image?.filename) return `Image ${this.index + 1}`;
    
    // Truncate long filenames
    const maxLength = 20;
    if (this.image.filename.length > maxLength) {
      return this.image.filename.substring(0, maxLength - 3) + '...';
    }
    return this.image.filename;
  }
  
  get thumbnailUrl(): string {
    // Priority: thumbnail_base64 (from API) -> thumbnailUrl -> url
    if (this.image?.thumbnail_base64) {
      // Base64 data from API - add data URL prefix if not present
      if (this.image.thumbnail_base64.startsWith('data:')) {
        return this.image.thumbnail_base64;
      }
      return `data:image/png;base64,${this.image.thumbnail_base64}`;
    }
    return this.image?.thumbnailUrl || this.image?.url || '';
  }
  
  get hasDbStatus(): boolean {
    return this.image?.savedToDb === true;
  }
  
  get hasGcsStatus(): boolean {
    return this.image?.uploadedToGcs === true;
  }
}
